import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { Bell, Ticket, Clock, AlertTriangle, ArrowUp, AtSign, FileText, Upload, CheckCircle, X, Mail, MousePointerClick, MessageSquare, BarChart3 } from 'lucide-react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { get, patch, del } from '@/lib/api'
import { Button } from '@/components/ui/Button'
import { EmptyState } from '@/components/ui/EmptyState'
import { cn } from '@/lib/utils'

interface Notification {
  id: string
  type: 'CASE_ASSIGNED' | 'CASE_UPDATED' | 'SLA_WARNING' | 'SLA_BREACH' | 'ESCALATION' | 'MENTION' | 'INVITE' | 'SYSTEM' | 'DIGEST' | 'FILE_READY' | 'IMPORT_DONE' | 'EMAIL_OPENED' | 'EMAIL_CLICKED' | 'EMAIL_REPLIED' | 'EMAIL_BOUNCED' | 'BULK_EMAIL_COMPLETE'
  title: string
  body: string
  link: string | null
  readAt: string | null
  createdAt: string
}

const ICONS: Record<Notification['type'], { icon: typeof Ticket; className: string }> = {
  CASE_ASSIGNED:  { icon: Ticket, className: 'text-violet' },
  CASE_UPDATED:   { icon: Ticket, className: 'text-info' },
  SLA_WARNING:    { icon: Clock, className: 'text-warning-text' },
  SLA_BREACH:     { icon: AlertTriangle, className: 'text-alert-text' },
  ESCALATION:     { icon: ArrowUp, className: 'text-alert-text' },
  MENTION:        { icon: AtSign, className: 'text-info' },
  INVITE:         { icon: Bell, className: 'text-violet' },
  SYSTEM:         { icon: Bell, className: 'text-muted' },
  DIGEST:         { icon: FileText, className: 'text-muted' },
  FILE_READY:     { icon: CheckCircle, className: 'text-success-text' },
  IMPORT_DONE:    { icon: Upload, className: 'text-success-text' },
  EMAIL_OPENED:        { icon: Mail, className: 'text-green-dark' },
  EMAIL_CLICKED:       { icon: MousePointerClick, className: 'text-violet' },
  EMAIL_REPLIED:       { icon: MessageSquare, className: 'text-info' },
  EMAIL_BOUNCED:       { icon: AlertTriangle, className: 'text-alert-text' },
  BULK_EMAIL_COMPLETE: { icon: BarChart3, className: 'text-violet' },
}

export function NotificationBell({ dark }: { dark?: boolean } = {}) {
  const [open, setOpen] = useState(false)
  const unread = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => get<{ count: number }>('/api/v1/notifications/unread-count', { background: true }),
    refetchInterval: 60_000,
  })
  const qc = useQueryClient()

  // Live updates via the shared socket — nothing to subscribe to if the user
  // isn't connected yet, so the effect is a no-op until the socket plugin
  // connects it in AppShell.
  useEffect(() => {
    const listener = (w: Window) => {
      const handler = () => { void qc.invalidateQueries({ queryKey: ['notifications'] }) }
      w.addEventListener('vela:notification', handler as EventListener)
      return () => w.removeEventListener('vela:notification', handler as EventListener)
    }
    return listener(window)
  }, [qc])

  const idleColor = dark ? 'rgba(255,255,255,0.55)' : '#64748B'
  const hoverColor = dark ? 'rgba(255,255,255,0.9)' : '#0F172A'
  const hoverBg = dark ? 'rgba(255,255,255,0.08)' : '#F8FAFC'

  const count = unread.data?.count ?? 0
  return (
    <>
      <button
        type="button"
        className="relative inline-flex h-9 w-9 items-center justify-center rounded-md"
        style={{ color: idleColor, transition: 'color 0.15s, background 0.15s' }}
        onMouseEnter={(e) => {
          e.currentTarget.style.color = hoverColor
          e.currentTarget.style.background = hoverBg
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.color = idleColor
          e.currentTarget.style.background = 'transparent'
        }}
        aria-label="Notifications"
        onClick={() => setOpen(true)}
      >
        <Bell style={{ height: 20, width: 20 }} />
        {count > 0 ? (
          <span
            className="absolute flex items-center justify-center font-bold"
            style={{
              top: -2,
              right: -2,
              height: 16,
              width: 16,
              minWidth: 16,
              borderRadius: '50%',
              background: '#E11D48',
              color: 'white',
              fontSize: 10,
            }}
          >
            {count > 99 ? '99+' : count}
          </span>
        ) : null}
      </button>
      <NotificationPanel open={open} onClose={() => setOpen(false)} />
    </>
  )
}

function NotificationPanel({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient()
  const list = useQuery({
    queryKey: ['notifications', 'list'],
    queryFn: () => get<Notification[]>('/api/v1/notifications'),
    enabled: open,
  })

  const markRead = useMutation({
    mutationFn: (id: string) => patch(`/api/v1/notifications/${id}/read`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
  const markAllRead = useMutation({
    mutationFn: () => patch('/api/v1/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })
  const hide = useMutation({
    mutationFn: (id: string) => del(`/api/v1/notifications/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['notifications'] }),
  })

  if (!open) return null

  const items = list.data ?? []
  const unreadCount = items.filter((n) => !n.readAt).length

  return createPortal(
    <div className="fixed inset-0 z-[2147483647] isolate">
      <div className="absolute inset-0 bg-[rgba(3,17,32,0.42)] backdrop-blur-[1px]" onClick={onClose} aria-hidden />
      <aside
        role="dialog"
        aria-label="Notifications"
        className="absolute right-0 top-0 z-[1] flex h-dvh w-full max-w-[400px] flex-col overflow-hidden border-l border-border bg-card shadow-pop sm:w-[400px]"
      >
        <header className="shrink-0 border-b border-border bg-card px-4 py-4">
          <div className="flex items-center justify-between gap-3">
          <h2 className="text-lg font-semibold text-heading">Notifications</h2>
          <div className="flex items-center gap-2">
            {unreadCount > 0 ? (
              <Button variant="link" size="sm" onClick={() => markAllRead.mutate()}>
                Mark all read
              </Button>
            ) : null}
            <Button variant="ghost" size="icon" aria-label="Close" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          </div>
        </header>
        <div className="min-h-0 flex-1 overflow-y-auto bg-card">
          {items.length === 0 ? (
            <div className="p-6">
              <EmptyState title="You're all caught up" message="Notifications about your cases and imports will appear here." />
            </div>
          ) : (
            <ul className="divide-y divide-border">
              {items.map((n) => {
                const { icon: Icon, className } = ICONS[n.type] ?? ICONS.SYSTEM
                const unread = !n.readAt
                const content = (
                  <div className={cn('flex gap-3 px-4 py-3 text-sm transition-colors hover:bg-page', unread && 'bg-vela-ice/70')}>
                    <Icon className={cn('mt-0.5 h-5 w-5 shrink-0', className)} />
                    <div className="min-w-0 flex-1">
                      <div className={cn('text-heading', unread ? 'font-semibold' : 'font-medium')}>{n.title}</div>
                      <div className="mt-0.5 text-muted leading-relaxed">{n.body}</div>
                      <div className="mt-1 text-xs text-muted">{new Date(n.createdAt).toLocaleString()}</div>
                    </div>
                    {unread ? <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-[var(--color-primary)]" /> : null}
                  </div>
                )
                return (
                  <li key={n.id} className="group relative">
                    {n.link ? (
                      <Link
                        to={n.link}
                        onClick={() => { markRead.mutate(n.id); onClose() }}
                      >
                        {content}
                      </Link>
                    ) : (
                      <button
                        type="button"
                        className="w-full text-left"
                        onClick={() => markRead.mutate(n.id)}
                      >
                        {content}
                      </button>
                    )}
                    <button
                      type="button"
                      aria-label="Dismiss"
                      className="absolute right-2 top-2 hidden h-6 w-6 items-center justify-center rounded-input text-muted hover:bg-border group-hover:flex"
                      onClick={() => hide.mutate(n.id)}
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      </aside>
    </div>,
    document.body,
  )
}
